import { fonts } from "@intentsify/brand";
import {
  createChartScene,
  renderChartSvg,
  StaticChartDefinition,
} from "@tanstack/charts";
import { useMemo } from "react";
import { useMeasure } from "react-use";
import { EXPORT_TITLE_HEIGHT } from "../Charts.const";
import { BrandChartTheme } from "./brandChartTheme";
import { LINE_LEGEND_SWATCH_WIDTH, LineLegendItem } from "./LineLegend";

const LEGEND_HEIGHT = 32;
const LEGEND_ITEM_GAP = 24;
/** Average glyph width of 13px Outfit legend labels. */
const LEGEND_CHAR_WIDTH = 7;
const LEGEND_INDENT = 22;

const itemWidth = (item: LineLegendItem) =>
  LINE_LEGEND_SWATCH_WIDTH + 8 + item.label.length * LEGEND_CHAR_WIDTH;

/**
 * Left-to-right legend positions, starting under the title's indent and
 * wrapping onto a new row when the next item would not fit.
 */
const layoutLegend = (legend: LineLegendItem[] = [], width: number) => {
  let x = LEGEND_INDENT;
  let row = 0;

  return legend.map((item) => {
    if (x > LEGEND_INDENT && x + itemWidth(item) > width - LEGEND_INDENT) {
      x = LEGEND_INDENT;
      row += 1;
    }

    const placed = { item, x, row };
    x += itemWidth(item) + LEGEND_ITEM_GAP;

    return placed;
  });
};

type ChartExportSvgProps = {
  /** Looked up by `useExportableChart` to save the SVG as PNG. */
  id?: string;
  title?: string;
  ariaLabel: string;
  brandTheme: BrandChartTheme;
  /** Reference-line keys drawn under the chart. */
  legend?: LineLegendItem[];
  buildDefinition: (size: {
    width: number;
    height: number;
  }) => StaticChartDefinition<any, any, any>;
};

/**
 * Static, explicitly sized render for PNG export: one `<svg id>` holding the
 * title and the chart scene, so `save-svg-as-png` can capture it.
 */
export const ChartExportSvg = ({
  id,
  title,
  ariaLabel,
  brandTheme,
  buildDefinition,
  legend,
}: ChartExportSvgProps) => {
  const [ref, { width, height }] = useMeasure<HTMLDivElement>();
  const titleHeight = title ? EXPORT_TITLE_HEIGHT : 0;
  const placedLegend = layoutLegend(legend, width);
  const legendRows = placedLegend.length
    ? Math.max(...placedLegend.map(({ row }) => row)) + 1
    : 0;
  const legendHeight = legendRows * LEGEND_HEIGHT;
  const chartHeight = height - titleHeight - legendHeight;

  const chartSvg = useMemo(() => {
    if (width <= 0 || chartHeight <= 0) {
      return null;
    }

    const size = { width, height: chartHeight };
    const scene = createChartScene(buildDefinition(size), size);

    // The renderer sizes its root to 100% of the parent viewport, which here
    // would include the title band; pin it to the scene size instead.
    return renderChartSvg(scene, { ariaLabel, idPrefix: id }).replace(
      'width="100%" height="100%"',
      `width="${size.width}" height="${size.height}"`,
    );
  }, [ariaLabel, buildDefinition, chartHeight, id, width]);

  return (
    <div ref={ref} style={{ width: "100%", height: "100%" }}>
      {chartSvg && (
        <svg
          id={id}
          width={width}
          height={height}
          style={{
            fontFamily: fonts.stack,
            color: brandTheme.theme.foreground,
          }}
        >
          {title && (
            <text
              x={22}
              y={30}
              dominantBaseline="middle"
              fontSize={22}
              fontWeight={500}
              fill={brandTheme.theme.foreground}
            >
              {title}
            </text>
          )}
          <g
            transform={`translate(0, ${titleHeight})`}
            dangerouslySetInnerHTML={{ __html: chartSvg }}
          />
          {placedLegend.map(({ item, x, row }) => (
            <g
              key={item.label}
              transform={`translate(${x}, ${
                titleHeight + chartHeight + LEGEND_HEIGHT * (row + 0.5)
              })`}
            >
              {item.swatch ? (
                <rect
                  x={LINE_LEGEND_SWATCH_WIDTH - 10}
                  y={-5}
                  width={10}
                  height={10}
                  rx={2}
                  fill={item.color}
                  fillOpacity={item.opacity}
                />
              ) : (
                <line
                  x1={0}
                  x2={LINE_LEGEND_SWATCH_WIDTH}
                  stroke={item.color}
                  strokeOpacity={item.opacity}
                  strokeWidth={item.strokeWidth ?? 1.5}
                  strokeDasharray={item.strokeDasharray}
                />
              )}
              <text
                x={LINE_LEGEND_SWATCH_WIDTH + 8}
                dominantBaseline="middle"
                fontSize={13}
                fill={brandTheme.theme.foreground}
              >
                {item.label}
              </text>
            </g>
          ))}
        </svg>
      )}
    </div>
  );
};
